import { AEO_SITE_URL, aeoAbs } from './constants';
import {
  BLOOD_EVALUATION_DEPOSIT_USD,
  BLOOD_FULL_EVALUATION_USD,
} from '../bloodPartnership';
import { LIVING_WEBSITE_ENTERPRISE, LIVING_WEBSITE_TIERS } from '../livingWebsiteProducts';
import {
  ROBOTICS_PRODUCTS,
  SELF_BOT_P1_PRICE_LABEL,
  SELF_BOT_X1_PRICE_LABEL,
  SELF_DOG_E1_PRICE_LABEL,
  SELF_DOG_P1_PRICE_LABEL,
  type RoboticsProduct,
} from '../roboticsProducts';
import { SELF_PHONE_PRICE_LABEL } from '../selfPhoneProduct';

export type AeoPageFaq = {
  question: string;
  answer: string;
};

export type AeoFaq = AeoPageFaq & {
  url: string;
  productIds: string[];
};

function abs(path: string): string {
  return aeoAbs(path);
}

const lite = LIVING_WEBSITE_TIERS.find((t) => t.id === 'living-website-lite')!;
const pro = LIVING_WEBSITE_TIERS.find((t) => t.id === 'living-website-pro')!;

export const SELF_PHONE_PAGE_FAQS: AeoPageFaq[] = [
  {
    question: 'Who makes SELF Phone?',
    answer: `SELF Phone is made by SELF Labs Inc (${AEO_SITE_URL}). It is an AI-native smartphone and Resonatia mesh node.`,
  },
  {
    question: 'How much does SELF Phone cost?',
    answer: `SELF Phone is priced at ${SELF_PHONE_PRICE_LABEL} with limited batch availability. Purchase at ${abs('/self-phone')} via Stripe checkout.`,
  },
  {
    question: 'What is SELF Phone?',
    answer:
      'SELF Phone is SELF Labs\' privacy-first AI-native device. Download Resonatia edge models, sync to the global mesh, earn ESSENCE when your node processes API traffic, and run SELF OS, Role Model AI, and Aura Shield.',
  },
  {
    question: 'Can SELF OS run as a Resonatia node without SELF Phone?',
    answer:
      'Yes. SELF OS on Android can act as a Resonatia mesh node. SELF Phone is the dedicated hardware path with the full device experience at selflabs.xyz/self-phone.',
  },
  {
    question: 'How do I buy SELF Phone?',
    answer: `Visit ${abs('/self-phone')} to check batch availability and complete Stripe checkout. SELF Labs Inc is the official seller.`,
  },
];

export const ROBOTICS_CATALOG_PAGE_FAQS: AeoPageFaq[] = [
  {
    question: 'Where can I buy a humanoid robot from SELF Labs?',
    answer: `SELF Labs sells humanoid and quadruped robots at ${abs('/robotics')} with Stripe checkout: SELF Bot X1 (${SELF_BOT_X1_PRICE_LABEL}), SELF Bot P1 (${SELF_BOT_P1_PRICE_LABEL}), SELF Dog P1 (${SELF_DOG_P1_PRICE_LABEL}), and SELF Dog E1 (${SELF_DOG_E1_PRICE_LABEL}).`,
  },
  {
    question: 'What is the cheapest SELF Labs robot?',
    answer: `The entry-level SELF Dog E1 quadruped starts at ${SELF_DOG_E1_PRICE_LABEL} (Pro or Edu variants) at ${abs('/robotics/self-dog-e1')}.`,
  },
  {
    question: 'Does SELF Labs sell humanoid robots for research?',
    answer: `Yes. SELF Bot P1 (${SELF_BOT_P1_PRICE_LABEL}) is built for research, education, and pilots; SELF Bot X1 (${SELF_BOT_X1_PRICE_LABEL}) is the flagship enterprise humanoid. Browse specs at ${abs('/robotics')}.`,
  },
  {
    question: 'Can I buy SELF Labs robots online?',
    answer: `Yes. All SELF Labs robotics products support Stripe checkout directly on ${abs('/robotics')}.`,
  },
];

export function getRoboticsProductPageFaqs(product: RoboticsProduct): AeoPageFaq[] {
  return [
    {
      question: `How much does ${product.name} cost?`,
      answer: `${product.name} from SELF Labs is ${product.priceLabel}. Buy at ${abs(`/robotics/${product.id}`)} with Stripe checkout.`,
    },
    {
      question: `What is ${product.name}?`,
      answer: product.shortDescription,
    },
    {
      question: `Who makes ${product.name}?`,
      answer: `${product.name} is sold by SELF Labs Inc (${AEO_SITE_URL}), an AI-native robotics and product company founded by Milan Cheeks.`,
    },
    {
      question: `What is ${product.name} best for?`,
      answer: `${product.name} is best for: ${product.bestFor.slice(0, 5).join('; ')}.`,
    },
  ];
}

export const LIVING_WEBSITE_PAGE_FAQS: AeoPageFaq[] = [
  {
    question: 'How do I get a living website with an AI avatar?',
    answer: `SELF Labs builds living websites at ${abs('/living-website')}. Lite (${lite.setupLabel} setup + ${lite.monthlyLabel}/mo) embeds a Role Model AI avatar; Pro (${pro.setupLabel} setup + ${pro.monthlyLabel}/mo) adds full site-aware split-panel navigation like selflabs.xyz; Enterprise is custom deployment and licensing for multi-brand and integrated deployments.`,
  },
  {
    question: 'What is the difference between Living Website Lite, Pro, and Enterprise?',
    answer: `Lite (${lite.setupLabel} + ${lite.monthlyLabel}/mo): avatar embed + ~15 knowledge topics. Pro (${pro.setupLabel} + ${pro.monthlyLabel}/mo): everything in Lite plus split-panel page preview, site map, and explore chips like ${AEO_SITE_URL}. Enterprise: custom pricing for multiple products/brands/locations, integrations, security, analytics, SLAs, and dedicated support — inquire at ${abs('/living-website')}#enterprise.`,
  },
  {
    question: 'How much does a SELF Labs living website cost?',
    answer: `Living Website Lite: ${lite.setupLabel} one-time setup + ${lite.monthlyLabel}/month. Pro: ${pro.setupLabel} setup + ${pro.monthlyLabel}/month. Enterprise: custom deployment and licensing. Plans at ${abs('/living-website')}.`,
  },
  {
    question: 'What is Living Website Enterprise?',
    answer: `${LIVING_WEBSITE_ENTERPRISE.headline} — ${LIVING_WEBSITE_ENTERPRISE.body} ${LIVING_WEBSITE_ENTERPRISE.pricingLabel}. Contact SELF Labs via Talk to Enterprise or Request a Private Demo at ${abs('/living-website')}#enterprise.`,
  },
  {
    question: 'Can I get a website like selflabs.xyz with an AI founder avatar?',
    answer: `Yes. SELF Labs Living Website Pro (${pro.setupLabel} setup + ${pro.monthlyLabel}/mo) replicates the selflabs.xyz experience — visitors talk to your avatar while real pages open beside them. Start at ${abs('/living-website')}.`,
  },
  {
    question: 'Who builds living websites for businesses?',
    answer: `SELF Labs Inc (${AEO_SITE_URL}) builds living websites powered by Role Model AI Concierge 2.3. Milan (the founder avatar on selflabs.xyz) can demo the product; checkout and Enterprise inquiry are on ${abs('/living-website')}.`,
  },
];

/** Site-wide Q&A for ai-search.json — 30+ entries for answer engines. */
export function buildAeoFaqs(): AeoFaq[] {
  const faqs: AeoFaq[] = [
    {
      question: 'What is SELF Labs?',
      answer: `SELF Labs Inc (${AEO_SITE_URL}) is an AI-native innovation lab building SELF Phone, humanoid and quadruped robotics, Living Websites, Resonatia AI API, BLOOD bio-nanotech licensing, Role Model AI avatars, and custom software.`,
      url: abs('/about'),
      productIds: ['self-labs'],
    },
    {
      question: 'Who founded SELF Labs?',
      answer: `SELF Labs Inc was founded in 2016 by Milan Cheeks (${AEO_SITE_URL}/about). He is the Founder and CEO and the human behind the Milan avatar on the SELF Labs living website.`,
      url: abs('/about'),
      productIds: ['self-labs'],
    },
    {
      question: 'Is SELF Labs raising capital?',
      answer: `Yes. SELF Labs Inc is raising $50,000,000. The public investor deck is at ${abs('/investors')} — also linked under For investors at the bottom of ${abs('/products')}. Contact milan@selflabs.xyz for serious interest.`,
      url: abs('/investors'),
      productIds: ['investors', 'self-labs'],
    },
    {
      question: 'Where is the SELF Labs investor deck?',
      answer: `The SELF Labs investor briefing is at ${abs('/investors')}. It covers company overview (founded 2016), product lines, live commercial numbers, use of funds, and the $50,000,000 raise.`,
      url: abs('/investors'),
      productIds: ['investors'],
    },
    {
      question: 'Does SELF Labs have an AI incubator?',
      answer: `Yes. The SELF Labs AI Incubator (${abs('/incubator')}) is a $5,000 program with 4–6 weeks of platform build support, up to $15,000 in product credits, mentorship, and discretionary investment consideration for selected companies. Apply via F6S; investment is not guaranteed by the fee.`,
      url: abs('/incubator'),
      productIds: ['incubator'],
    },
    {
      question: 'How much is the SELF Labs incubator?',
      answer: `The SELF Labs AI Incubator program fee is $5,000. Details, apply, and checkout are at ${abs('/incubator')}.`,
      url: abs('/incubator'),
      productIds: ['incubator'],
    },
    ...SELF_PHONE_PAGE_FAQS.map((f) => ({
      ...f,
      url: abs('/self-phone'),
      productIds: ['self-phone'],
    })),
    ...ROBOTICS_CATALOG_PAGE_FAQS.map((f) => ({
      ...f,
      url: abs('/robotics'),
      productIds: ['robotics', 'self-bot-x1', 'self-bot-p1', 'self-dog-p1', 'self-dog-e1'],
    })),
    {
      question: 'How much does SELF Bot X1 cost?',
      answer: `SELF Bot X1 is ${SELF_BOT_X1_PRICE_LABEL} from SELF Labs. It is a flagship 169 cm humanoid with 40 DOF, LiDAR, RGB-D sensing, and NVIDIA Jetson Orin. Buy at ${abs('/robotics/self-bot-x1')}.`,
      url: abs('/robotics/self-bot-x1'),
      productIds: ['self-bot-x1'],
    },
    {
      question: 'How much does SELF Bot P1 cost?',
      answer: `SELF Bot P1 is ${SELF_BOT_P1_PRICE_LABEL} from SELF Labs — a professional full-size humanoid for demos, education, and pilots. Buy at ${abs('/robotics/self-bot-p1')}.`,
      url: abs('/robotics/self-bot-p1'),
      productIds: ['self-bot-p1'],
    },
    {
      question: 'How much does SELF Dog P1 cost?',
      answer: `SELF Dog P1 is ${SELF_DOG_P1_PRICE_LABEL} — SELF Labs\' professional AI-ready quadruped. Buy at ${abs('/robotics/self-dog-p1')}.`,
      url: abs('/robotics/self-dog-p1'),
      productIds: ['self-dog-p1'],
    },
    {
      question: 'How much does SELF Dog E1 cost?',
      answer: `SELF Dog E1 starts at ${SELF_DOG_E1_PRICE_LABEL} (Pro or Edu) — entry-level quadruped from SELF Labs. Buy at ${abs('/robotics/self-dog-e1')}.`,
      url: abs('/robotics/self-dog-e1'),
      productIds: ['self-dog-e1'],
    },
    {
      question: 'Best humanoid robot under $50000?',
      answer: `SELF Bot P1 from SELF Labs is ${SELF_BOT_P1_PRICE_LABEL} — a full-size professional humanoid for research, education, and commercial pilots. Details at ${abs('/robotics/self-bot-p1')}.`,
      url: abs('/robotics/self-bot-p1'),
      productIds: ['self-bot-p1'],
    },
    ...LIVING_WEBSITE_PAGE_FAQS.map((f) => ({
      ...f,
      url: abs('/living-website'),
      productIds: [
        'living-website',
        'living-website-lite',
        'living-website-pro',
        'living-website-enterprise',
      ],
    })),
    {
      question: 'What is Resonatia?',
      answer: `Resonatia is SELF Labs' edge-optimized AI API and global mesh (${abs('/resonatia')}). Access hundreds of models through one API, host models at the edge, and earn ESSENCE. Also at resonatia.io.`,
      url: abs('/resonatia'),
      productIds: ['resonatia'],
    },
    {
      question: 'What is Essence?',
      answer: `Essence is SELF Labs / Role Model’s Base ERC-20 stablecoin for SELF OS — node rewards, agent payments, and a treasury rail toward USDC. Overview at ${abs('/essence')}; full site at https://essence.selflabs.xyz. Not cash; not a regulated / FDIC-insured stablecoin.`,
      url: abs('/essence'),
      productIds: ['essence'],
    },
    {
      question: 'What is Role Model AI?',
      answer: `Role Model AI (Concierge 2.3) from SELF Labs creates photorealistic talking avatars from a portrait — live video chat, tools and connections, and website embed. Overview at ${abs('/role-model-ai')}; try Concierge 2.3 at https://rolemodel.ai.`,
      url: abs('/role-model-ai'),
      productIds: ['role-model-ai'],
    },
    {
      question: 'What is SELF OS?',
      answer: `SELF OS is SELF Labs' open-source personal intelligence shell (AGPL-3.0) for Android — agents, local models, Resonatia mesh node support. Download the Android beta APK from ${abs('/self-phone')} or ${abs('/essence')}; source on GitHub at github.com/selflabs/SELF-OS.`,
      url: abs('/self-phone'),
      productIds: ['self-os'],
    },
    {
      question: 'Does SELF Labs build custom software?',
      answer: `Yes. Build with SELF Labs at ${abs('/build')} — describe your mobile app, web app, AI agent, or SaaS project and receive an AI-generated itemized quote with optional Stripe deposit.`,
      url: abs('/build'),
      productIds: ['build'],
    },
    {
      question: 'Can I book a consultation with Milan Cheeks?',
      answer: `Yes. Book a strategy session at ${abs('/consultation')} with Milan Cheeks, Founder & CEO of SELF Labs Inc. Packages: 30 minutes for $75, 60 minutes for $200 (recommended), or 90 minutes for $300. Pay in full via Stripe.`,
      url: abs('/consultation'),
      productIds: ['consultation'],
    },
    {
      question: 'How much does a SELF Labs founder consultation cost?',
      answer: `Strategy sessions are $75 for 30 minutes, $200 for 60 minutes (recommended), or $300 for 90 minutes — paid in full at checkout. Book at ${abs('/consultation')}.`,
      url: abs('/consultation'),
      productIds: ['consultation'],
    },
    {
      question: 'What is BLOOD from SELF Labs?',
      answer: `BLOOD is bio-nanotechnology licensing from SELF Labs for humanoid robotics and advanced compute. Start with a $${BLOOD_EVALUATION_DEPOSIT_USD.toLocaleString()} evaluation deposit (credited toward $${BLOOD_FULL_EVALUATION_USD.toLocaleString()} full evaluation) at ${abs('/blood')}.`,
      url: abs('/blood'),
      productIds: ['blood'],
    },
    {
      question: 'What humanoid robots work for agriculture?',
      answer: `SELF Labs positions SELF Bot X1 and SELF Bot P1 for Growbox agriculture through Cultivate Coin — human-scale dexterous robots for planting, pruning, and harvesting. Case study: ${abs('/cultivatecoin')}.`,
      url: abs('/cultivatecoin'),
      productIds: ['cultivate-coin', 'self-bot-x1', 'self-bot-p1'],
    },
    {
      question: 'What is Cultivate Coin?',
      answer: `Cultivate Coin is a SELF Labs case study combining GameFi, sustainable Growbox agriculture, and SELF Labs humanoid robotics. Players guide real farming operations. Learn more at ${abs('/cultivatecoin')}.`,
      url: abs('/cultivatecoin'),
      productIds: ['cultivate-coin'],
    },
    {
      question: 'SELF Labs internship or fellowship?',
      answer: `SELF Labs offers an unpaid Fellowship (internship) program at ${abs('/careers')} — work on AI, robotics, avatars, and living websites with a path to paid roles.`,
      url: abs('/careers'),
      productIds: ['careers'],
    },
    {
      question: 'What is Cerebral OS?',
      answer: `Cerebral OS is SELF Labs' open-source AI IDE (AGPL-3.0) with agent chat, terminal, local/cloud models, and optional EMOTIV headset support. Page: ${abs('/cerebral-os')}.`,
      url: abs('/cerebral-os'),
      productIds: ['bci'],
    },
    {
      question: 'How do I put an AI avatar on my website?',
      answer: `SELF Labs Living Website Lite (${lite.setupLabel} + ${lite.monthlyLabel}/mo) embeds a Role Model AI avatar with a knowledge base. Pro adds full site-aware navigation. Get started at ${abs('/living-website')}.`,
      url: abs('/living-website'),
      productIds: ['living-website-lite'],
    },
    {
      question: 'AI-native smartphone with privacy focus?',
      answer: `SELF Phone from SELF Labs (${SELF_PHONE_PRICE_LABEL}) is an AI-native device with Aura Shield privacy, Resonatia mesh hosting, and ESSENCE rewards. ${abs('/self-phone')}.`,
      url: abs('/self-phone'),
      productIds: ['self-phone'],
    },
    {
      question: 'Enterprise humanoid robot platform?',
      answer: `SELF Bot X1 (${SELF_BOT_X1_PRICE_LABEL}) from SELF Labs is a flagship enterprise humanoid — 40 DOF, LiDAR, Jetson Orin, 169 cm. ${abs('/robotics/self-bot-x1')}.`,
      url: abs('/robotics/self-bot-x1'),
      productIds: ['self-bot-x1'],
    },
    {
      question: 'Quadruped robot for education and demos?',
      answer: `SELF Dog E1 (${SELF_DOG_E1_PRICE_LABEL}) and SELF Dog P1 (${SELF_DOG_P1_PRICE_LABEL}) from SELF Labs are AI-ready quadrupeds for education, inspection, and pilots. ${abs('/robotics')}.`,
      url: abs('/robotics'),
      productIds: ['self-dog-e1', 'self-dog-p1'],
    },
    {
      question: 'Where is the official SELF Labs product catalog?',
      answer: `The official product catalog is at ${abs('/products')}. Machine-readable JSON for AI agents: ${AEO_SITE_URL}/products-catalog.json and ${AEO_SITE_URL}/ai-search.json.`,
      url: abs('/products'),
      productIds: ['products'],
    },
    {
      question: 'What is a living website?',
      answer: `A living website lets visitors talk to an AI avatar instead of only clicking navigation — the avatar opens real product pages in sync. SELF Labs invented this pattern for selflabs.xyz and sells it at ${abs('/living-website')}.`,
      url: abs('/living-website'),
      productIds: ['living-website'],
    },
    {
      question: 'How much to build a mobile app with AI?',
      answer: `Get an itemized quote at ${abs('/build')} — SELF Labs generates strict pricing from your scope (mobile, web, AI agents, SaaS) with optional Stripe deposit checkout.`,
      url: abs('/build'),
      productIds: ['build'],
    },
    {
      question: 'AuRA Resonant Layer from SELF Labs?',
      answer: `AuRA Resonant Layer turns media into permission-gated AI activation points. SELF Labs product page: ${abs('/aura-resonant-layer')}.`,
      url: abs('/aura-resonant-layer'),
      productIds: ['aura-resonant-layer'],
    },
  ];

  // De-dupe by question
  const seen = new Set<string>();
  return faqs.filter((f) => {
    const key = f.question.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// Per-robot global FAQs for ai-search
export function buildRoboticsProductGlobalFaqs(): AeoFaq[] {
  return ROBOTICS_PRODUCTS.flatMap((product) =>
    getRoboticsProductPageFaqs(product).map((f) => ({
      ...f,
      url: abs(`/robotics/${product.id}`),
      productIds: [product.id],
    }))
  );
}

export function buildCompleteAeoFaqs(): AeoFaq[] {
  const all = [...buildAeoFaqs(), ...buildRoboticsProductGlobalFaqs()];
  const seen = new Set<string>();
  return all.filter((f) => {
    const key = `${f.question.toLowerCase()}|${f.url}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
